import IconButton from "@material-ui/core/IconButton";
import { makeStyles } from "@material-ui/core/styles";
import { fade } from "@material-ui/core/styles/colorManipulator";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import CloseIcon from "@material-ui/icons/Close";
import Debounce, { DebounceProps } from "@saleor/components/Debounce";
import ArrowDropdownIcon from "@saleor/icons/ArrowDropdown";
import { FetchMoreProps } from "@saleor/types";
import classNames from "classnames";
import Downshift, { ControllerStateAndHelpers } from "downshift";
import { filter } from "fuzzaldrin";
import React from "react";

import MultiAutocompleteSelectFieldContent, {
  MultiAutocompleteActionType
} from "./MultiAutocompleteSelectFieldContentNew";

const useStyles = makeStyles(
  theme => ({
    chip: {
      width: "100%"
    },
    chipClose: {
      color: "#1e3f7e",
      height: 32,
      padding: 0,
      width: 32
    },
    chipContainer: {
      display: "flex",
      flexDirection: "column",
      marginTop: theme.spacing(1)
    },
    chipContainerResponsive: {
      maxHeight: "30vh",
      overflowY: "auto"
    },
    chipInner: {
      "& svg": {
        color: "theme.palette.primary.contrastText"
      },
      alignItems: "center",
      background: "#aecbfa",
      borderRadius: 18,
      color: theme.palette.primary.contrastText,
      display: "flex",
      justifyContent: "space-between",
      margin: theme.spacing(1, 0),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1)
    },
    chipLabel: {
      color: "#1e3f7e",
      fontWeight: "bold"
    },
    container: {
      flexGrow: 1,
      position: "relative"
    },
    disabledChipInner: {
      "& svg": {
        color: theme.palette.grey[200]
      },
      alignItems: "center",
      background: fade(theme.palette.grey[400], 0.8),
      borderRadius: 18,
      color: theme.palette.primary.contrastText,
      display: "flex",
      justifyContent: "space-between",
      margin: theme.spacing(1, 0),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(1)
    }
  }),
  { name: "MultiAutocompleteSelectField" }
);

export interface MultiAutocompleteSelectFieldNewProps
  extends Partial<FetchMoreProps> {
  add?: MultiAutocompleteActionType;
  allowCustomValues?: boolean;
  displayValues: any[];
  error?: boolean;
  name: string;
  choices: any[];
  value: any[];
  loading?: boolean;
  placeholder?: string;
  helperText?: string;
  label?: string;
  id?: string;
  responsiveHeight?: boolean;
  fetchChoices?: (value: string) => void;
  onChange: (event: React.ChangeEvent<any>) => void;
  onHandleSelectAll?: (value: boolean) => void;
  selectAll?: boolean;
  hasSelectAll?: boolean;
}

const DebounceAutocomplete: React.ComponentType<DebounceProps<
  string
>> = Debounce;

const MultiAutocompleteSelectFieldNewComponent: React.FC<MultiAutocompleteSelectFieldNewProps> = props => {
  const {
    id,
    add,
    allowCustomValues,
    choices,
    displayValues,
    error,
    hasMore,
    helperText,
    label,
    loading,
    name,
    placeholder,
    value,
    responsiveHeight,
    fetchChoices,
    onChange,
    onFetchMore,
    hasSelectAll,
    onHandleSelectAll,
    selectAll,
    ...rest
  } = props;
  const classes = useStyles(props);

  const handleSelect = (
    item: string,
    downshiftOpts?: ControllerStateAndHelpers
  ) => {
    if (downshiftOpts) {
      downshiftOpts.reset({ inputValue: "" });
    }
    onChange({
      target: { name, value: item }
    } as any);
  };

  return (
    <>
      <Downshift
        onInputValueChange={fetchChoices}
        onSelect={handleSelect}
        itemToString={() => ""}
      >
        {({
          closeMenu,
          getInputProps,
          getItemProps,
          isOpen,
          toggleMenu,
          highlightedIndex,
          inputValue
        }) => {
          const displayCustomValue =
            inputValue &&
            inputValue.length > 0 &&
            allowCustomValues &&
            !choices.find(
              choice => choice.label.toLowerCase() === inputValue.toLowerCase()
            );

          return (
            <div className={classes.container} {...rest}>
              <TextField
                InputProps={{
                  ...getInputProps({
                    placeholder
                  }),
                  endAdornment: (
                    <div>
                      <ArrowDropdownIcon onClick={() => toggleMenu()} />
                    </div>
                  ),
                  id: undefined,
                  onClick: toggleMenu
                }}
                error={error}
                helperText={helperText}
                label={label}
                fullWidth={true}
              />
              {isOpen && (!!inputValue || !!choices.length) && (
                <MultiAutocompleteSelectFieldContent
                  add={
                    add && {
                      ...add,
                      onClick: () => {
                        add.onClick();
                        closeMenu();
                      }
                    }
                  }
                  choices={choices.filter(
                    choice => !value.includes(choice.value)
                  )}
                  displayCustomValue={displayCustomValue}
                  displayValues={displayValues}
                  getItemProps={getItemProps}
                  hasMore={hasMore}
                  highlightedIndex={highlightedIndex}
                  loading={loading}
                  inputValue={inputValue}
                  onFetchMore={onFetchMore}
                  hasSelectAll={hasSelectAll}
                  onHandleSelectAll={() => {
                    onHandleSelectAll(!selectAll);
                    closeMenu();
                  }}
                  selectAll={selectAll}
                />
              )}
            </div>
          );
        }}
      </Downshift>
      <div
        className={classNames(classes.chipContainer, {
          [classes.chipContainerResponsive]: responsiveHeight
        })}
      >
        {displayValues.map(value => (
          <div className={classes.chip} key={value.value}>
            <div
              className={
                !value.disabled ? classes.chipInner : classes.disabledChipInner
              }
            >
              <Typography className={classes.chipLabel}>
                {value.label}
              </Typography>

              <IconButton
                className={classes.chipClose}
                disabled={value.disabled}
                onClick={() => handleSelect(value.value)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

const MultiAutocompleteSelectFieldNew: React.FC<MultiAutocompleteSelectFieldNewProps> = ({
  choices,
  fetchChoices,
  ...props
}) => {
  const [query, setQuery] = React.useState("");

  if (fetchChoices) {
    return (
      <DebounceAutocomplete debounceFn={fetchChoices}>
        {debounceFn => (
          <MultiAutocompleteSelectFieldNewComponent
            choices={choices}
            {...props}
            fetchChoices={debounceFn}
          />
        )}
      </DebounceAutocomplete>
    );
  }

  return (
    <MultiAutocompleteSelectFieldNewComponent
      fetchChoices={q => setQuery(q || "")}
      choices={filter(choices, query, {
        key: "label"
      })}
      {...props}
    />
  );
};

MultiAutocompleteSelectFieldNew.displayName = "MultiAutocompleteSelectFieldNew";
export default MultiAutocompleteSelectFieldNew;
